import { Box, Button, Checkbox, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiAmountWithCurrency } from '../api';
import { useAuth } from '../hooks/useAuth';
import { formatAmountInCurrency } from '../utils';
import { IPointLoyalty } from '../utils/types';
import AmountInput from './AmountInput';

type OrderListItemProps = {
  order: number;
  firstName: string;
  lastName: string;
  tourName: string;
  tourStartDate: string;
  bedTypeName: string;
  pnr: string;
  orderNumber: string;
  selected?: boolean;
  onSelection?: (v: boolean) => void;
  readonly?: boolean;
  balance: ApiAmountWithCurrency;
  total: ApiAmountWithCurrency;
  received: ApiAmountWithCurrency;
  toPay: ApiAmountWithCurrency;
  loyalty?: IPointLoyalty;
  onPayWithLoyaltyClick?: () => void;
  onRemoveLoyaltyPoints?: () => void;
  onChangeAmount: (val: number) => void;
};

export function OrderCard(props: OrderListItemProps) {
  const { t } = useTranslation();
  const { token } = useAuth();

  const formattedDate = (date?: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    };

    if (date) return new Date(date).toLocaleDateString('tr-TR', dateOptions);
    return '-';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
        width: '190px',
        maxWidth: '190px',
        minWidth: 'min-content',
        border: '2px solid #DBDBDB',
        borderRadius: '10px',
      }}
    >
      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            pointerEvents: props.readonly ? 'none' : 'auto',
            opacity: props.readonly ? '0.5' : '1',
          }}
        >
          <Typography
            sx={{ color: 'inherit' }}
            onClick={() =>
              props.onSelection &&
              props.onSelection(props.selected ? false : true)
            }
          >
            {props.firstName || props.lastName
              ? `${props.firstName} ${props.lastName}`
              : '-'}
          </Typography>
          <Checkbox
            value={props.orderNumber}
            key={props.orderNumber}
            checked={props.selected}
            onChange={() =>
              props.onSelection &&
              props.onSelection(props.selected ? false : true)
            }
            disabled={props.readonly}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <AmountInput
            value={props.toPay.amount}
            onChange={props.onChangeAmount}
            currencySymbol={
              formatAmountInCurrency(
                props.toPay.amount,
                props.toPay.currency?.code || 'TRY',
              )[0]
            }
            width={150}
            label={t('orderCard.paymentAmount')}
            disabled={props.readonly}
          />
        </Box>

        {(props.loyalty?.fiat || 0) > 0 &&
          props.loyalty?.currencyExchangeRate && (
            <Box sx={{ display: 'flex', alignItems: 'center', px: 4, mt: 3 }}>
              <AmountCard
                amount={
                  props.loyalty.fiat / props.loyalty.currencyExchangeRate || 0
                }
                currencyCode={'TRY'}
                label={t('orderCard.usedLoyaltyPoints')}
              />
            </Box>
          )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            my: 3,
            gap: '24px',
          }}
        >
          <AmountCard
            amount={props.total.amount}
            currencyCode={props.total.currency?.code || 'TRY'}
            label={t('orderCard.orderAmount')}
          />
          <AmountCard
            amount={props.received.amount}
            currencyCode={props.received.currency?.code || 'TRY'}
            label={t('orderCard.paymenyMade')}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#F8F8F8',
            borderTop: '2px solid #DBDBDB',
            fontSize: '12px',
            textAlign: 'center',
            height: '100%',
            mx: '-8px',
            p: 1,
            borderRadius: !(token && props.selected) ? '0 0 10px 10px' : '0',
            mb: !(token && props.selected) ? '-8px' : '0',
          }}
        >
          <Typography sx={{ fontSize: '14px' }}>
            {props.tourName && props.bedTypeName
              ? `${props.tourName} | ${props.bedTypeName}`
              : '-'}
          </Typography>
          <Typography sx={{ color: 'inherit', fontSize: '14px', mt: 2 }}>
            {`${formattedDate(props.tourStartDate) || ''}`}
          </Typography>
        </Box>
      </Box>
      {props.selected && token && (
        <Button
          color={props.loyalty?.points ? 'error' : 'primary'}
          size="small"
          variant="contained"
          sx={{
            fontSize: 14,
            width: '100%',
            borderRadius: '0 0 10px 10px',
            height: '32px',
          }}
          onClick={
            props.loyalty?.points
              ? props.onRemoveLoyaltyPoints
              : props.onPayWithLoyaltyClick
          }
        >
          {props.loyalty?.points
            ? t('orderCard.cancelPaymentWithPoint')
            : t('orderCard.paymentWithPoints')}
        </Button>
      )}
    </Box>
  );
}

function AmountCard(props: {
  amount: number;
  currencyCode: string;
  label: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: '#FAFAFA',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '28px',
          borderRadius: '5px',
          border: '2px solid #DBDBDB',
          lineHeight: '30px',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            width: '100%',
            px: 2,
            fontSize: '12px',
            overflow: 'hidden',
            textAlign: 'center',
          }}
        >
          {formatAmountInCurrency(props.amount, props.currencyCode)}
        </Typography>
      </Box>
      <Typography
        variant="inherit"
        sx={{ fontSize: '9px', whiteSpace: 'nowrap' }}
      >
        {props.label}
      </Typography>
    </Box>
  );
}
